import { useEffect, useState } from 'react';
import { getSiteContent } from '../services/storage';
import type { Language } from '../types/lang.types';
import type { AboutContent, TeamMember } from '../pages/public/UeberUnsPage';

/*
 * „Über uns“: Struktur, Reihenfolge und Bilder kommen aus dem deutschen Inhalt (about_us).
 * about_us_en / about_us_ar enthalten nur Übersetzungen der Texte, gleich sortiert wie DE.
 * Fehlt eine Übersetzung, wird der deutsche Text angezeigt.
 */
export const ABOUT_KEY = 'about_us';
export const aboutKeyFor = (lang: Language): string => (lang === 'de' ? ABOUT_KEY : `${ABOUT_KEY}_${lang}`);

type Partial2<T> = { [K in keyof T]?: T[K] extends (infer U)[] ? Partial<U>[] : T[K] };
export type AboutTranslation = Partial2<AboutContent>;

const pick = (tr: unknown, de: string): string => (typeof tr === 'string' && tr.trim() !== '' ? tr : de);
const pickList = (tr: unknown, de: string[] | undefined): string[] | undefined =>
  Array.isArray(tr) && tr.some((s) => typeof s === 'string' && s.trim() !== '') ? (tr as string[]) : de;

export function localizeAbout(de: AboutContent, tr: AboutTranslation | null | undefined): AboutContent {
  const team = (de.team ?? [])
    .map((m, i) => ({ m, t: tr?.team?.[i] ?? {} }))
    .filter(({ m }) => !m.hidden)
    .map(({ m, t }): TeamMember => ({
      ...m,
      // Der Name bleibt deutsch – er bestimmt die URL der Team-Seite
      qualification: pick(t.qualification, m.qualification ?? ''),
      role: pick(t.role, m.role),
      desc: pick(t.desc, m.desc),
      ausbildung: pickList(t.ausbildung, m.ausbildung),
      expertise: pickList(t.expertise, m.expertise),
      berufserfahrung: pickList(t.berufserfahrung, m.berufserfahrung),
    }));
  if (!tr) return { ...de, team };
  return {
    ...de,
    hero_title: pick(tr.hero_title, de.hero_title),
    hero_subtitle: pick(tr.hero_subtitle, de.hero_subtitle),
    intro_label: pick(tr.intro_label, de.intro_label),
    intro_title: pick(tr.intro_title, de.intro_title),
    intro_text: pick(tr.intro_text, de.intro_text),
    cta_title: pick(tr.cta_title, de.cta_title),
    cta_text: pick(tr.cta_text, de.cta_text),
    values: (de.values ?? []).map((v, i) => ({
      ...v,
      title: pick(tr.values?.[i]?.title, v.title),
      desc: pick(tr.values?.[i]?.desc, v.desc),
    })),
    team,
  };
}

const isFilled = (data: unknown): data is Record<string, unknown> =>
  !!data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0;

/** Lädt „Über uns“ in der gewünschten Sprache; null solange noch geladen wird */
export function useAboutContent(lang: Language, defaults: AboutContent): AboutContent | null {
  const [content, setContent] = useState<AboutContent | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      getSiteContent(ABOUT_KEY).catch(() => null),
      lang === 'de' ? Promise.resolve(null) : getSiteContent(aboutKeyFor(lang)).catch(() => null),
    ]).then(([de, tr]) => {
      if (!active) return;
      const base = isFilled(de) ? ({ ...defaults, ...de } as AboutContent) : defaults;
      setContent(localizeAbout(base, isFilled(tr) ? (tr as AboutTranslation) : null));
    });
    return () => { active = false; };
  }, [lang, defaults]);

  return content;
}
